import React, { useState } from 'react';
import { ThemeProvider } from '@mui/system';
import {
  Tooltip,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import { useDispatch, useSelector } from 'react-redux';

import * as DefaultRoutes from '@Routes/DefaultRoutes';
import GenericRnDListing from '@Pages/RnDMember/GenericRnDListing/GenericRnDListing';
import { EXEMPTIONS_FOR_APPROVAL, EXIT_PLAN_APPROVE } from '@API/APIRoutes';
import { setSnackbar } from '@Redux/snackBarSlice';
import axiosInstance from '@API/axios';
import { buildExemptionColumns, DEFAULT_EXEMPTION_COLUMN_VISIBILITY } from '@Pages/Exemptions/ExemptionsHub/exemptionColumns';

const PAGE_TITLE = 'Exemptions to Approve';
const ERROR_MESSAGE = 'Failed to fetch exemption requests. Trying again!';

/** Replaces the approver's "Plans to Approve" directory: SUBMITTED exemption requests awaiting the caller's decision. */
function ExemptionsPendingApproval() {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.user.userId);

  const [decisionDialogOpen, setDecisionDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [decision, setDecision] = useState('APPROVE');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const columns = buildExemptionColumns({ actionsWidth: 165 });

  const openDecisionDialog = (row, value) => {
    setSelected(row);
    setDecision(value);
    setComment('');
    setDecisionDialogOpen(true);
  };

  const closeDecisionDialog = () => {
    setDecisionDialogOpen(false);
    setSelected(null);
    setDecision('APPROVE');
    setComment('');
  };

  const handleConfirmDecision = async () => {
    if (!selected) return;
    setSubmitting(true);
    const approved = decision === 'APPROVE';
    try {
      await axiosInstance.post(EXIT_PLAN_APPROVE(selected.id), { approved, comment });
      dispatch(setSnackbar({
        open: true,
        message: approved ? 'Exemption request approved successfully!' : 'Exemption request rejected successfully!',
        error: false,
      }));
      closeDecisionDialog();
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      dispatch(setSnackbar({
        open: true,
        message: err?.response?.data?.message || 'Failed to process decision. Please try again.',
        error: true,
      }));
      closeDecisionDialog();
    } finally {
      setSubmitting(false);
    }
  };

  const renderActions = (params, navigate, theme) => {
    const row = params.row;
    const myApproval = (row.approvals || []).find((a) => String(a.approver?.id) === String(userId));
    return (
      <ThemeProvider theme={theme}>
        {myApproval ? (
          <Tooltip title={myApproval.approved ? 'You already approved this request' : 'You already rejected this request'}>
            <Chip
              label={myApproval.approved ? 'You approved' : 'You rejected'}
              color={myApproval.approved ? 'success' : 'error'}
              size="small"
              variant="outlined"
            />
          </Tooltip>
        ) : (
          <>
            <Tooltip title="Approve Exemption Request">
              <IconButton color="success" size="small" onClick={() => openDecisionDialog(row, 'APPROVE')}>
                <CheckCircleIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reject Exemption Request">
              <IconButton color="error" size="small" onClick={() => openDecisionDialog(row, 'REJECT')}>
                <CancelRoundedIcon />
              </IconButton>
            </Tooltip>
          </>
        )}
        <Tooltip title="Open Exemption Request">
          <IconButton
            color="info"
            size="small"
            onClick={() => navigate(DefaultRoutes.exemptionInfo.replace(':planId', row.id))}
          >
            <LightbulbOutlinedIcon />
          </IconButton>
        </Tooltip>
      </ThemeProvider>
    );
  };

  return (
    <>
      <GenericRnDListing
        key={refreshKey}
        title={PAGE_TITLE}
        PageIcon={<FactCheckRoundedIcon color="white" fontSize="large" />}
        iconColor="success.main"
        apiRoute={EXEMPTIONS_FOR_APPROVAL}
        defaultColumns={columns}
        renderActions={renderActions}
        errorMessage={ERROR_MESSAGE}
        buttons={[]}
        getRowId={(row) => row.id}
        initialColumnVisibilityModel={DEFAULT_EXEMPTION_COLUMN_VISIBILITY}
        serverPagination={false}
      />

      <Dialog open={decisionDialogOpen} onClose={closeDecisionDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{decision === 'APPROVE' ? 'Approve Exemption Request' : 'Reject Exemption Request'}</DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            {`Are you sure you want to ${decision === 'APPROVE' ? 'approve' : 'reject'} the exemption request for ingredient "${selected?.ingredientName || ''}"?`}
          </Typography>
          <TextField
            label={decision === 'APPROVE' ? 'Approval Comment (optional)' : 'Rejection Reason'}
            fullWidth
            multiline
            minRows={4}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDecisionDialog} disabled={submitting}>Cancel</Button>
          <Button
            variant="contained"
            color={decision === 'APPROVE' ? 'success' : 'error'}
            onClick={handleConfirmDecision}
            disabled={submitting || (decision === 'REJECT' && !comment.trim())}
            startIcon={submitting ? <CircularProgress size={20} /> : null}
          >
            {submitting ? 'Submitting...' : decision === 'APPROVE' ? 'Confirm Approval' : 'Confirm Rejection'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ExemptionsPendingApproval;
